/**
 * ContactsProvider — single source of truth for contacts + groups.
 *
 * On mount: reads from IndexedDB (instant), then syncs from server in background.
 * All pages consume from this context instead of making independent API calls.
 * Mutations update local state + IDB first, then push to server.
 */
import { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { contactsAPI, groupsAPI } from './api';
import { useAuth } from './auth';
import * as db from './contactsDb';

const ContactsContext = createContext(null);

export function ContactsProvider({ children }) {
  const { user } = useAuth();
  const userId = user?.id || null;
  const [contacts, setContacts] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [boundUserId, setBoundUserId] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const lastSyncRef = useRef(0);
  const userIdRef = useRef(userId);
  userIdRef.current = userId;

  const stillThisAccount = (accountId) => userIdRef.current === accountId;

  // ── Bootstrap: this account's IDB → render → server sync ──
  // Depend on the id only. A visibility probe replaces the user object
  // without changing who is signed in, and must not blank the book.
  useEffect(() => {
    if (!userId) {
      setContacts([]);
      setGroups([]);
      setLoaded(false);
      setBoundUserId(null);
      db.clearAll();
      return undefined;
    }
    let cancelled = false;
    // Drop the previous account before the async read. The render below
    // also hides rows whose bound id does not match, including the paint
    // that happens before this effect runs.
    setContacts([]);
    setGroups([]);
    setLoaded(false);
    setBoundUserId(null);
    lastSyncRef.current = 0;

    const boot = async () => {
      const cached = await db.readCacheForUser(userId);
      if (cancelled || !stillThisAccount(userId)) return;
      if (cached) {
        setContacts(cached.contacts);
        setGroups(cached.groups);
        setBoundUserId(userId);
        setLoaded(true);
      }

      // Sync from server with a short retry — the first call right after
      // login can hit a cold backend pod. Don't retry an auth failure.
      const fetchWithRetry = async () => {
        const attempt = () => Promise.all([contactsAPI.list(), groupsAPI.list()]);
        try {
          return await attempt();
        } catch (err) {
          if (err?.response?.status === 401) throw err;
          await new Promise((r) => setTimeout(r, 1200));
          return attempt();
        }
      };
      try {
        setSyncing(true);
        const [cRes, gRes] = await fetchWithRetry();
        if (cancelled || !stillThisAccount(userId)) return;
        setContacts(cRes.data);
        setGroups(gRes.data);
        setBoundUserId(userId);
        setLoaded(true);
        lastSyncRef.current = Date.now();
        await db.writeCacheForUser(userId, cRes.data, gRes.data);
      } catch {
        // No same-account cache and no server answer: stay unloaded so
        // Home keeps the skeleton instead of painting another account
        // or a false empty book.
      } finally {
        if (!cancelled && stillThisAccount(userId)) setSyncing(false);
      }
    };

    boot();
    return () => { cancelled = true; };
  }, [userId]);

  // ── Force refresh from server ──
  const refresh = useCallback(async () => {
    const accountId = userIdRef.current;
    if (!accountId) return;
    try {
      setSyncing(true);
      const [cRes, gRes] = await Promise.all([
        contactsAPI.list(),
        groupsAPI.list(),
      ]);
      if (!stillThisAccount(accountId)) return;
      setContacts(cRes.data);
      setGroups(gRes.data);
      setBoundUserId(accountId);
      setLoaded(true);
      lastSyncRef.current = Date.now();
      await db.writeCacheForUser(accountId, cRes.data, gRes.data);
    } finally {
      if (stillThisAccount(accountId)) setSyncing(false);
    }
  }, []);

  // Window-focus + online recovery (Session 120) — when the user comes
  // back to the tab or regains network, force a sync if our cached data
  // is more than 10s stale. This is the second-line defence against the
  // "first-load hiccup" cold-start race.
  //
  // Important: skip the refresh if the user is currently typing into an
  // input/textarea on the page. On mobile Safari, the on-screen keyboard
  // appearing or dismissing can fire a window `focus` event, which
  // previously triggered a full contacts refetch mid-edit. That refetch
  // caused a wide-tree re-render that, on slower devices, was racing with
  // controlled-input keystrokes and producing the "I can only type one
  // character at a time" symptom users were hitting on contact edit
  // forms. The deferred sync happens on the next focus event after the
  // user blurs the input.
  useEffect(() => {
    if (!user) return;
    const isUserTyping = () => {
      const el = (typeof document !== 'undefined') ? document.activeElement : null;
      if (!el) return false;
      const tag = el.tagName;
      return tag === 'INPUT' || tag === 'TEXTAREA' || el.isContentEditable === true;
    };
    const onFocusOrOnline = () => {
      if (isUserTyping()) return;
      if (Date.now() - lastSyncRef.current > 10_000) {
        refresh().catch(() => { /* silent — boot's retry will cover */ });
      }
    };
    window.addEventListener('focus', onFocusOrOnline);
    window.addEventListener('online', onFocusOrOnline);
    return () => {
      window.removeEventListener('focus', onFocusOrOnline);
      window.removeEventListener('online', onFocusOrOnline);
    };
  }, [user, refresh]);

  // ── Refresh only if stale (>30s since last sync) ──
  const refreshIfStale = useCallback(async () => {
    if (Date.now() - lastSyncRef.current > 30_000) {
      await refresh();
    }
  }, [refresh]);

  // ── Local mutations (update context + IDB, caller handles API) ──
  const updateContactLocal = useCallback((id, data) => {
    const accountId = userIdRef.current;
    if (!accountId) return;
    setContacts(prev => {
      if (userIdRef.current !== accountId) return prev;
      const updated = prev.map(c => c.id === id ? { ...c, ...data } : c);
      db.writeContactsForUser(accountId, updated);
      return updated;
    });
  }, []);

  const addContactLocal = useCallback((contact) => {
    const accountId = userIdRef.current;
    if (!accountId) return;
    setContacts(prev => {
      if (userIdRef.current !== accountId) return prev;
      const next = [contact, ...prev];
      db.writeContactsForUser(accountId, next);
      return next;
    });
  }, []);

  const removeContactLocal = useCallback((id) => {
    const accountId = userIdRef.current;
    if (!accountId) return;
    setContacts(prev => {
      if (userIdRef.current !== accountId) return prev;
      const next = prev.filter(c => c.id !== id);
      db.writeContactsForUser(accountId, next);
      return next;
    });
  }, []);

  const updateGroupLocal = useCallback((id, data) => {
    const accountId = userIdRef.current;
    if (!accountId) return;
    setGroups(prev => {
      if (userIdRef.current !== accountId) return prev;
      const updated = prev.map(g => g.id === id ? { ...g, ...data } : g);
      db.writeGroupsForUser(accountId, updated);
      return updated;
    });
  }, []);

  const addGroupLocal = useCallback((group) => {
    const accountId = userIdRef.current;
    if (!accountId) return;
    setGroups(prev => {
      if (userIdRef.current !== accountId) return prev;
      const next = [...prev, group];
      db.writeGroupsForUser(accountId, next);
      return next;
    });
  }, []);

  const setGroupsLocal = useCallback((nextGroups) => {
    const accountId = userIdRef.current;
    if (!accountId) return;
    setGroups(nextGroups);
    db.writeGroupsForUser(accountId, nextGroups);
  }, []);

  // ── Clear on logout ──
  const clearStore = useCallback(() => {
    setContacts([]);
    setGroups([]);
    setLoaded(false);
    db.clearAll();
  }, []);

  // Hide rows until they are bound to the signed-in account. The id can
  // change one paint before the effect clears state; this gate covers that
  // paint, a delayed response, and a second tab that still holds the old list.
  const sameAccount = !!userId && boundUserId === userId;
  const value = useMemo(() => ({
    contacts: sameAccount ? contacts : [],
    groups: sameAccount ? groups : [],
    loaded: sameAccount && loaded,
    syncing,
    refresh, refreshIfStale,
    updateContactLocal, addContactLocal, removeContactLocal,
    updateGroupLocal, addGroupLocal, setGroupsLocal,
    clearStore,
  }), [contacts, groups, loaded, sameAccount, syncing,
    refresh, refreshIfStale,
    updateContactLocal, addContactLocal, removeContactLocal,
    updateGroupLocal, addGroupLocal, setGroupsLocal, clearStore]);

  return (
    <ContactsContext.Provider value={value}>
      {children}
    </ContactsContext.Provider>
  );
}

export function useContacts() {
  const ctx = useContext(ContactsContext);
  if (!ctx) throw new Error('useContacts must be used within ContactsProvider');
  return ctx;
}
